import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row, Spinner, Alert, Table, Badge, ProgressBar, InputGroup, Form } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatNumber } from "../utils/formatters";
import './StockCritique.css';

export default function StockCritique() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    fetchStockCritique();
    
    // Auto-refresh toutes les 30 secondes si activé
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchStockCritique, 30000);
    }
    return () => clearInterval(interval);
  }, [autoRefresh]);

  const fetchStockCritique = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      
      // Utilisation de votre domaine API
      const response = await fetch(
        `https://sawd.julesferry-ga.com/api/stock_critique.php`,
        {
          method: 'GET',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (!response.ok) {
        if (response.status === 401) {
          // Token expiré ou invalide
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          navigate("/pages/signin2");
          throw new Error("Session expirée. Veuillez vous reconnecter.");
        }
        throw new Error(`Erreur HTTP: ${response.status}`);
      }
      
      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error("Erreur de chargement:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchStockCritique();
  };

  const handleCreateOrder = (produit) => {
    // Rediriger vers la création de commande fournisseur
    navigate(`/achats/commandesNouveau?produit=${produit.id_produit}&quantite=${produit.quantite_manquante}`);
  };

const handleBulkOrder = () => {
    const produitsSelectionnes = filteredProduits.filter(p => p.selected);
    if (produitsSelectionnes.length > 0) {
        const produitsData = produitsSelectionnes.map(p => ({
            id: p.id_produit,
            quantite: p.quantite_manquante || 1 // ou la quantité souhaitée
        }));
        navigate('/achats/commandesNouveau', { 
            state: { 
                produits: produitsData,
                type: 'bulk_order' 
            } 
        });
    }  
};

  // Filtrer les produits
  const filteredProduits = data?.produits?.filter(produit => {
    const matchesSearch = produit.designation.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         produit.reference.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' || produit.niveau_alerte === filter;
    const matchesCategory = selectedCategory === 'all' || produit.nom_categorie === selectedCategory;
    return matchesSearch && matchesFilter && matchesCategory;
  }) || [];

  // Obtenir les catégories uniques
  const categories = [...new Set(data?.produits?.map(p => p.nom_categorie) || [])].filter(Boolean);

  // Obtenir le badge pour le niveau d'alerte
  const getNiveauBadge = (niveau) => {
    const badges = {
      'rupture': { bg: 'danger', text: 'Rupture', icon: 'ri-alert-line' },
      'critique': { bg: 'warning', text: 'Critique', icon: 'ri-alert-line' },
      'alerte': { bg: 'info', text: 'Alarme', icon: 'ri-information-line' }
    };
    return badges[niveau] || badges.alerte;
  };

  // Configuration des graphiques
  const seriesCategories = {
    series: data?.categories?.map(c => c.nb_produits) || [],
    options: {
      chart: {
        type: 'pie',
        height: 350,
        toolbar: { show: false }
      },
      labels: data?.categories?.map(c => c.nom_categorie || 'Sans catégorie') || [],
      colors: ['#ef4444', '#f59e0b', '#3b82f6', '#10b981', '#8b5cf6', '#ec4899'],
      legend: { position: 'bottom' },
      tooltip: {
        y: {
          formatter: (value) => `${value} produit(s)`
        }
      },
      plotOptions: {
        pie: {
          donut: {
            labels: {
              show: true,
              total: {
                show: true,
                label: 'Total',
                formatter: () => data?.produits?.length || 0
              }
            }
          }
        }
      },
      responsive: [{
        breakpoint: 480,
        options: {
          chart: { width: '100%' },
          legend: { position: 'bottom' }
        }
      }]
    }
  };

  const seriesHistorique = [{
    name: 'Alertes',
    data: data?.historique?.map(h => h.nb_alertes).reverse() || []
  }];

  const optionsHistorique = {
    chart: {
      type: 'area',
      height: 200,
      toolbar: { show: false },
      sparkline: { enabled: false }
    },
    colors: ['#ef4444'],
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.7,
        opacityTo: 0.2
      }
    },
    xaxis: {
      categories: data?.historique?.map(h => {
        const date = new Date(h.date_alerte);
        return `${date.getDate()}/${date.getMonth() + 1}`;
      }).reverse() || [],
      labels: { rotate: -45 }
    },
    yaxis: {
      title: { text: 'Nombre d\'alertes' }
    },
    tooltip: {
      y: { formatter: (value) => `${value} alerte(s)` }
    },
    dataLabels: { enabled: false }
  };

  if (loading && !data) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" variant="danger" />
        <span className="ms-3">Analyse du stock critique...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <Alert variant="danger">
          <Alert.Heading>Erreur de chargement</Alert.Heading>
          <p>{error}</p>
          <hr />
          <div className="d-flex justify-content-end">
            <Button variant="outline-danger" onClick={handleRefresh}>
              <i className="ri-refresh-line me-2"></i>
              Réessayer
            </Button>
          </div>
        </Alert>
      </div>
    );
  }

  return (
    <React.Fragment>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        {/* En-tête avec alertes */}
        <div className="d-md-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="/dashboard">Tableau de bord</Link></li>
              <li className="breadcrumb-item active" aria-current="page">Stock critique</li>
            </ol>
            <h4 className="main-title mb-0 d-flex align-items-center">
              <i className="ri-alert-line text-danger me-2"></i>
              Surveillance du stock critique
              {(data?.statistiques?.nb_ruptures > 0 || data?.statistiques?.nb_critiques > 0) && (
                <Badge bg="danger" className="ms-3">
                  {data.statistiques.nb_ruptures} rupture{data.statistiques.nb_ruptures > 1 ? 's' : ''}
                </Badge>
              )}
            </h4>
          </div>
          <div className="d-flex gap-2 mt-3 mt-md-0">
            <div className="form-check form-switch me-3 d-flex align-items-center">
              <input
                className="form-check-input me-2"
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                id="autoRefresh"
              />
              <label className="form-check-label small" htmlFor="autoRefresh">
                Auto-refresh
              </label>
            </div>
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={handleRefresh}
              disabled={loading}
              className="d-flex align-items-center gap-1"
            >
              <i className={`ri-refresh-line ${loading ? 'fa-spin' : ''}`}></i>
              Actualiser
            </Button>
            {/* <Button 
              variant="success" 
              size="sm"
              onClick={handleBulkOrder}
              disabled={!filteredProduits.some(p => p.selected)}
              className="d-flex align-items-center gap-1"
            >
              <i className="ri-shopping-cart-line"></i>
              Commander sélection
            </Button> */}
          </div>
        </div>

        {/* KPI Cards */}
        <Row className="g-3 mb-4">
          <Col sm="6" xl="3">
            <Card className="card-one h-100 border-0 shadow-sm border-start border-danger border-4">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="kpi-icon bg-danger-soft rounded-circle p-3 me-3">
                    <i className="ri-alert-line fs-24 text-danger"></i>
                  </div>
                  <div>
                    <h6 className="text-secondary mb-1">Rupture de stock</h6>
                    <h3 className="mb-0 fw-bold text-danger">{data?.statistiques?.nb_ruptures || 0}</h3>
                    <small className="text-secondary">produits</small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col sm="6" xl="3">
            <Card className="card-one h-100 border-0 shadow-sm border-start border-warning border-4">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="kpi-icon bg-warning-soft rounded-circle p-3 me-3">
                    <i className="ri-alert-line fs-24 text-warning"></i>
                  </div>
                  <div>
                    <h6 className="text-secondary mb-1">Stock critique</h6>
                    <h3 className="mb-0 fw-bold text-warning">{data?.statistiques?.nb_critiques || 0}</h3>
                    <small className="text-secondary">produits</small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col sm="6" xl="3">
            <Card className="card-one h-100 border-0 shadow-sm border-start border-info border-4">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="kpi-icon bg-info-soft rounded-circle p-3 me-3">
                    <i className="ri-information-line fs-24 text-info"></i>
                  </div>
                  <div>
                    <h6 className="text-secondary mb-1">Stock en alerte</h6>
                    <h3 className="mb-0 fw-bold text-info">{data?.statistiques?.nb_alertes || 0}</h3>
                    <small className="text-secondary">produits</small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col sm="6" xl="3">
            <Card className="card-one h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="kpi-icon bg-primary-soft rounded-circle p-3 me-3">
                    <i className="ri-money-euro-circle-line fs-24 text-primary"></i>
                  </div>
                  <div>
                    <h6 className="text-secondary mb-1">Valeur réappro.</h6>
                    <h5 className="mb-0 fw-bold">{formatCurrency(data?.statistiques?.valeur_reapprovisionnement || 0)}</h5>
                    <small className="text-secondary">investissement nécessaire</small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Graphiques et répartition */}
        <Row className="g-3 mb-4">
          <Col xl="4">
            <Card className="border-0 shadow-sm h-100">
              <Card.Header className="bg-transparent border-0">
                <Card.Title as="h6" className="mb-0">Répartition par catégorie</Card.Title>
              </Card.Header>
              <Card.Body>
                {data?.categories?.length > 0 ? (
                  <>
                    <ReactApexChart 
                      series={seriesCategories.series} 
                      options={seriesCategories.options} 
                      type="donut" 
                      height={300} 
                    />
                    <div className="mt-3">
                      {data?.categories?.map((cat, idx) => (
                        <div key={idx} className="d-flex justify-content-between align-items-center mb-2">
                          <span className="small">{cat.nom_categorie || 'Sans catégorie'}</span>
                          <Badge bg="secondary">{cat.nb_produits}</Badge>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-5">
                    <i className="ri-pie-chart-2-line fs-48 text-secondary mb-3"></i>
                    <p>Aucune catégorie concernée</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col xl="8">
            <Card className="border-0 shadow-sm">
              <Card.Header className="bg-transparent border-0 d-flex align-items-center">
                <Card.Title as="h6" className="mb-0">Historique des alertes (30 jours)</Card.Title>
              </Card.Header>
              <Card.Body>
                <ReactApexChart 
                  series={seriesHistorique} 
                  options={optionsHistorique} 
                  type="area" 
                  height={250} 
                />
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filtres et recherche */}
        <Card className="border-0 shadow-sm mb-4">
          <Card.Body>
            <Row className="g-3">
              <Col md="4">
                <InputGroup>
                  <InputGroup.Text>
                    <i className="ri-search-line"></i>
                  </InputGroup.Text>
                  <Form.Control
                    placeholder="Rechercher un produit..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Col>
              <Col md="3">
                <Form.Select value={filter} onChange={(e) => setFilter(e.target.value)}>
                  <option value="all">Tous les niveaux</option>
                  <option value="rupture">Rupture</option>
                  <option value="critique">Critique</option>
                  <option value="alerte">Alarme</option>
                </Form.Select>
              </Col>
              <Col md="3">
                <Form.Select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
                  <option value="all">Toutes catégories</option>
                  {categories.map((cat, idx) => (
                    <option key={idx} value={cat}>{cat}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md="2">
                <div className="text-end text-secondary">
                  <small>{filteredProduits.length} produit(s)</small>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Tableau des produits critiques */}
        <Card className="border-0 shadow-sm mb-4">
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="align-middle mb-0">
                <thead className="table-light">
                  <tr>
                    <th width="40">
                      <Form.Check 
                        type="checkbox"
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setData(prev => ({
                            ...prev,
                            produits: prev.produits.map(p => ({ ...p, selected: checked }))
                          }));
                        }}
                      />
                    </th>
                    <th>Référence</th>
                    <th>Produit</th>
                    {/* <th>Catégorie</th> */}
                    <th className="text-end">Stock actuel</th>
                    <th className="text-end">Seuil</th>
                    <th className="text-end">Manquant</th>
                    <th>Emplacement</th>
                    <th>Niveau</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProduits.length > 0 ? (
                    filteredProduits.map((produit) => {
                      const badge = getNiveauBadge(produit.niveau_alerte);
                      const manquant = produit.seuil_alerte - produit.quantite_reelle;
                      const pourcentage = (produit.quantite_reelle / produit.seuil_alerte) * 100;
                      
                      return (
                        <tr key={produit.id_produit} className={produit.niveau_alerte === 'rupture' ? 'table-danger' : ''}>
                          <td>
                            <Form.Check 
                              type="checkbox"
                              checked={produit.selected || false}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setData(prev => ({
                                  ...prev,
                                  produits: prev.produits.map(p => 
                                    p.id_produit === produit.id_produit ? { ...p, selected: checked } : p
                                  )
                                }));
                              }}
                            />
                          </td>
                          <td>
                            <Badge bg="light" text="dark" className="font-mono">
                              {produit.reference}
                            </Badge>
                          </td>
                          <td>
                            <div className="fw-semibold">{produit.designation}</div>
                            {produit.code_barre && (
                              <small className="text-secondary">{produit.code_barre}</small>
                            )}
                          </td>
                          {/* <td>{produit.nom_categorie || '-'}</td> */}
                          <td className="text-end">
                            <span className={`fw-bold ${produit.quantite_reelle <= 0 ? 'text-danger' : ''}`}>
                              {formatNumber(produit.quantite_reelle)}
                            </span>
                            <div style={{ width: '80px', display: 'inline-block', marginLeft: '10px' }}>
                              <ProgressBar 
                                now={pourcentage} 
                                variant={pourcentage <= 0 ? 'danger' : pourcentage <= 30 ? 'warning' : 'info'}
                                style={{ height: '6px' }}
                              />
                            </div>
                          </td>
                          <td className="text-end">{produit.seuil_alerte}</td>
                          <td className="text-end text-danger fw-bold">
                            {manquant > 0 ? formatNumber(manquant) : '-'}
                          </td>
                          <td>{produit.emplacement || '-'}</td>
                          <td>
                            <Badge bg={badge.bg} className="d-flex align-items-center gap-1" style={{ width: 'fit-content' }}>
                              <i className={badge.icon}></i>
                              {badge.text}
                            </Badge>
                          </td>
                         <td className="text-end">
  <div className="d-flex justify-content-end gap-2" style={{ minWidth: '80px' }}>
    {/* Bouton Voir le produit - stylisé */}
    <Button 
      variant="light"
      size="sm"
      onClick={() => navigate(`/stock/produits/${produit.id_produit}`)}
      title="Voir le produit"
      className="action-btn-light"
      style={{
        width: '32px',
        height: '32px',
        borderRadius: '8px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8f9fa',
        border: '1px solid #e9ecef',
        color: '#0d6efd',
        transition: 'all 0.2s',
        padding: 0
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = '#0d6efd';
        e.currentTarget.style.borderColor = '#0d6efd';
        e.currentTarget.style.color = 'white';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = '#f8f9fa';
        e.currentTarget.style.borderColor = '#e9ecef';
        e.currentTarget.style.color = '#0d6efd';
      }}
    >
      <i className="ri-eye-line"></i>
    </Button>

    {/* Bouton Créer une commande - stylisé */}
    <Button 
      variant="light"
      size="sm"
      onClick={() => handleCreateOrder(produit)}
      title="Créer une commande"
      className="action-btn-light"
      style={{
        width: '32px',
        height: '32px',
        borderRadius: '8px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8f9fa',
        border: '1px solid #e9ecef',
        color: '#198754',
        transition: 'all 0.2s',
        padding: 0
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = '#198754';
        e.currentTarget.style.borderColor = '#198754';
        e.currentTarget.style.color = 'white';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = '#f8f9fa';
        e.currentTarget.style.borderColor = '#e9ecef';
        e.currentTarget.style.color = '#198754';
      }}
    >
      <i className="ri-shopping-cart-line"></i>
    </Button>
  </div>
</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="10" className="text-center py-5">
                        <i className="ri-checkbox-circle-line fs-48 text-success mb-3 d-block"></i>
                        <h5>Aucun produit en stock critique</h5>
                        <p className="text-secondary mb-0">Tous les stocks sont à des niveaux acceptables</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>

        {/* Suggestions de réapprovisionnement */}
        {data?.suggestions?.length > 0 && (
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-transparent border-0">
              <Card.Title as="h6" className="mb-0 d-flex align-items-center">
                <i className="ri-lightbulb-line text-warning me-2"></i>
                Suggestions de réapprovisionnement
              </Card.Title>
            </Card.Header>
            <Card.Body>
              <Row>
                {data.suggestions.slice(0, 3).map((suggestion, idx) => (
                  <Col md="4" key={idx}>
                    <Card className="border-0 bg-light">
                      <Card.Body>
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <div>
                            <Badge bg="primary" className="mb-2">{suggestion.reference}</Badge>
                            <h6 className="mb-1">{suggestion.designation}</h6>
                            <small className="text-secondary">{suggestion.fournisseur || 'Fournisseur non spécifié'}</small>
                          </div>
                          <i className="ri-shopping-cart-line fs-24 text-primary"></i>
                        </div>
                        <div className="d-flex justify-content-between mb-2">
                          <span>À commander:</span>
                          <span className="fw-bold text-danger">{formatNumber(suggestion.quantite_a_commander)}</span>
                        </div>
                        <div className="d-flex justify-content-between mb-3">
                          <span>Coût total:</span>
                          <span className="fw-bold text-primary">{formatCurrency(suggestion.cout_total)}</span>
                        </div>
                        <Button 
                          variant="outline-primary" 
                          size="sm" 
                          className="w-100"
                          onClick={() => handleCreateOrder(suggestion)}
                        >
                          <i className="ri-shopping-cart-line me-2"></i>
                          Commander
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
              {data.suggestions.length > 3 && (
                <div className="text-center mt-3">
                  <Link to="/achats/commandes/suggestions" className="text-decoration-none">
                    Voir toutes les suggestions ({data.suggestions.length})
                  </Link>
                </div>
              )}
            </Card.Body>
          </Card>
        )}

        <Footer />
      </div>
    </React.Fragment>
  );
}